'use client'

import { useId, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from './button'
import { ShareQR } from './share-qr'

type EventQRDisclosureProps = {
  /** Path only, e.g. `/events/abc` — forwarded to ShareQR. */
  path: string
  size?: number
  /** Offer the PNG export once revealed (facilitators/admins only). */
  downloadable?: boolean
  downloadFileName?: string
}

/**
 * Collapsed-by-default disclosure around the event share QR.
 *
 * The event QR used to render expanded on every event page, which put a second
 * scannable code next to the attendee's own check-in QR — at the door people
 * could not tell which one to present. It now hides behind one explicit
 * control, so any QR on screen by default is the one that gets scanned.
 *
 * A real <button> with aria-expanded/aria-controls, so it is keyboard reachable
 * and announced as a disclosure. The chevron rotation is the only motion and it
 * is dropped under prefers-reduced-motion.
 */
export function EventQRDisclosure({
  path,
  size,
  downloadable,
  downloadFileName,
}: EventQRDisclosureProps) {
  const [open, setOpen] = useState(false)
  const panelId = useId()

  return (
    <div className="flex flex-col items-center gap-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide Event QR code' : 'Show Event QR code'}
        <ChevronDown
          aria-hidden
          className={`transition-transform motion-reduce:transition-none ${
            open ? 'rotate-180' : ''
          }`}
        />
      </Button>
      {/* Kept out of the tree entirely while collapsed — a hidden-but-mounted
          QR is still findable by screen readers and page search. */}
      <div id={panelId}>
        {open ? (
          <ShareQR
            path={path}
            size={size}
            downloadable={downloadable}
            downloadFileName={downloadFileName}
          />
        ) : null}
      </div>
    </div>
  )
}
